// ─────────────────────────────────────────────────────────────
// SharedStudentData.js
// Single source of truth for all student data across the app.
// Used by both AdminDashboard and TeacherPlatform.
// ─────────────────────────────────────────────────────────────

// ── ACTIVITY & BADGE REGISTRY ─────────────────────────────────
// TO ADD A NEW ACTIVITY: add one line below with name, color, icon.
// TO ADD A NEW AUTO BADGE: add an entry to AUTO_BADGES with a check() function.
export const ACTIVITY_STYLES = {
  // Extracurricular
  "Debate Team":              { color:"#7C3AED", icon:"🎤" },
  "Soapbox Team":             { color:"#6D28D9", icon:"🎙️" },
  "Student Council":          { color:"#1560BD", icon:"🏛️" },
  "Town Hall Leaders":        { color:"#0891B2", icon:"🗣️" },
  "Theater Club":             { color:"#DB2777", icon:"🎭" },
  "ASPIRA":                   { color:"#DC2626", icon:"⭐" },
  "Art Club":                 { color:"#9333EA", icon:"🎨" },
  "Math Olympiad":            { color:"#1560BD", icon:"🧮" },
  "Choir":                    { color:"#BE185D", icon:"🎵" },
  "Robotics Club":            { color:"#0F766E", icon:"🤖" },
  "Science Club":             { color:"#0369A1", icon:"🔬" },
  "Book Club":                { color:"#92400E", icon:"📖" },
  "Chess Club":               { color:"#374151", icon:"♟️" },
  "Dance Team":               { color:"#DB2777", icon:"💃" },
  // Intervention & Target Programs
  "Afterschool Target Group": { color:"#EA580C", icon:"🎯" },
  "7% Target Group":          { color:"#D97706", icon:"📌" },
  "Bubble Group":             { color:"#059669", icon:"🫧" },
  // ── ADD NEW ACTIVITIES HERE ──────────────────────────────────
  // "Soccer Team": { color:"#16A34A", icon:"⚽" },
};

// Auto badges — awarded automatically from student data, no manual entry needed
export const AUTO_BADGES = [
  {
    id:      "honor_roll",
    label:   "Honor Roll",
    icon:    "🏅",
    color:   "#D97706",
    check:   (s) => s.att >= 90 && s.ela_score >= 490 && s.ela_growth >= 5,
    tooltip: "Attendance ≥90% · iReady ELA ≥490 · Growth ≥5pts",
  },
  {
    id:      "perfect_attendance",
    label:   "Perfect Attendance",
    icon:    "🌟",
    color:   "#059669",
    check:   (s) => s.att >= 98,
    tooltip: "Attendance ≥98%",
  },
  {
    id:      "most_improved",
    label:   "Most Improved",
    icon:    "📈",
    color:   "#0891B2",
    check:   (s) => s.ela_growth >= 18 || s.math_growth >= 18,
    tooltip: "iReady growth ≥18 points in ELA or Math",
  },
  {
    id:      "on_track_l4",
    label:   "On Track for L4",
    icon:    "🚀",
    color:   "#7C3AED",
    check:   (s) => {
      const elaSim  = s.sims.find(x=>x.subject==="ELA");
      const mathSim = s.sims.find(x=>x.subject==="Math");
      return (elaSim?.sim3 >= 85 && s.ela_score >= 530) ||
             (mathSim?.sim3 >= 85 && s.math_score >= 530);
    },
    tooltip: "Sim score ≥85% and iReady ≥530 in ELA or Math",
  },
  // ── ADD YOUR OWN AUTO BADGE BELOW ───────────────────────────
  // {
  //   id: "attendance_star", label: "Attendance Star", icon: "⭐", color: "#F59E0B",
  //   check: (s) => s.att >= 95,
  //   tooltip: "Attendance ≥95%",
  // },
];

export function getAutoBadges(student) {
  return AUTO_BADGES.filter(b => { try { return b.check(student); } catch(e) { return false; } });
}

// ── FULL STUDENT ROSTER ───────────────────────────────────────
// Each student has: academic data, IEP info, teacher, activities, and sims
export const ALL_STUDENTS = [
  {
    id:"S01", dob:"2012-09-14", name:"Alex M.",    grade:6, teacher:"C. Brooks",   teacherId:"TCH004",
    ela_level:"2 Below", ela_score:445, ela_growth:-2,
    math_level:"3 Below", math_score:432, math_growth:-5,
    att:86.7, chronic:true,  iep:true,  ell:false, ml:false, target:true,
    iepDate:"2025-10-18", iepGoal:"Increase ORF to 90 WCPM; improve reading comprehension to grade level",
    nys_ela:1, nys_math:1, risk:"High",
    ela_gaps:["Reading Comprehension","Vocabulary","Fluency"],
    math_gaps:["Number Sense","Fractions","Algebra"],
    ela_strengths:["Phonics & Decoding"],
    math_strengths:[],
    sims:[
      { subject:"ELA",  sim1:38, sim2:41, sim3:43, predicted_nys:1 },
      { subject:"Math", sim1:35, sim2:38, sim3:40, predicted_nys:1 },
    ],
    activities:["Afterschool Target Group","7% Target Group"],
  },
  {
    id:"S02", dob:"2014-11-03", name:"Jordan K.",  grade:4, teacher:"J. Thompson", teacherId:"TCH002",
    ela_level:"1 Below", ela_score:478, ela_growth:3,
    math_level:"On",     math_score:502, math_growth:8,
    att:94.9, chronic:false, iep:false, ell:false, ml:false, target:false,
    nys_ela:2, nys_math:2, risk:"Medium",
    ela_gaps:["Reading Comprehension","Fluency"],
    math_gaps:["Fractions"],
    ela_strengths:["Phonological Awareness","Vocabulary"],
    math_strengths:["Number Sense","Operations"],
    sims:[
      { subject:"ELA",  sim1:58, sim2:61, sim3:64, predicted_nys:2 },
      { subject:"Math", sim1:62, sim2:65, sim3:69, predicted_nys:3 },
    ],
    activities:["Theater Club","Student Council"],
  },
  {
    id:"S03", dob:"2011-07-22", name:"Sam L.",     grade:7, teacher:"C. Brooks",   teacherId:"TCH004",
    ela_level:"2 Below", ela_score:441, ela_growth:-4,
    math_level:"1 Below", math_score:461, math_growth:2,
    att:88.9, chronic:true,  iep:false, ell:false, ml:true,  target:true,
    nys_ela:1, nys_math:2, risk:"High",
    ela_gaps:["Reading Comprehension","Vocabulary","Grammar & Conventions"],
    math_gaps:["Fractions","Geometry"],
    ela_strengths:[],
    math_strengths:["Number Sense"],
    sims:[
      { subject:"ELA",  sim1:36, sim2:39, sim3:38, predicted_nys:1 },
      { subject:"Math", sim1:54, sim2:56, sim3:59, predicted_nys:2 },
    ],
    activities:["ASPIRA","Afterschool Target Group","7% Target Group"],
  },
  {
    id:"S04", dob:"2013-04-08", name:"Riley P.",   grade:5, teacher:"A. Patel",    teacherId:"TCH003",
    ela_level:"On",      ela_score:510, ela_growth:12,
    math_level:"1 Below", math_score:468, math_growth:5,
    att:97.2, chronic:false, iep:true,  ell:false, ml:false, target:false,
    iepDate:"2025-10-08", iepGoal:"Master fraction operations; achieve 80% on grade-level math assessments",
    nys_ela:2, nys_math:2, risk:"Low",
    ela_gaps:["Writing","Grammar & Conventions"],
    math_gaps:["Fractions","Algebra"],
    ela_strengths:["Fluency","Vocabulary","Reading Comprehension"],
    math_strengths:["Number Sense","Operations"],
    sims:[
      { subject:"ELA",  sim1:67, sim2:71, sim3:74, predicted_nys:3 },
      { subject:"Math", sim1:49, sim2:53, sim3:57, predicted_nys:2 },
    ],
    activities:["Debate Team","Town Hall Leaders"],
  },
  {
    id:"S05", dob:"2010-12-17", name:"Casey W.",   grade:8, teacher:"A. Patel",    teacherId:"TCH003",
    ela_level:"1 Above", ela_score:541, ela_growth:18,
    math_level:"On",     math_score:516, math_growth:14,
    att:98.3, chronic:false, iep:false, ell:false, ml:false, target:false,
    nys_ela:3, nys_math:3, risk:"Low",
    ela_gaps:[],
    math_gaps:[],
    ela_strengths:["Reading Comprehension","Writing","Vocabulary"],
    math_strengths:["Algebra","Geometry","Statistics"],
    sims:[
      { subject:"ELA",  sim1:74, sim2:78, sim3:81, predicted_nys:3 },
      { subject:"Math", sim1:71, sim2:75, sim3:78, predicted_nys:3 },
    ],
    activities:["Student Council","Debate Team","Math Olympiad"],
  },
  {
    id:"S06", dob:"2015-02-28", name:"Morgan D.",  grade:3, teacher:"M. Rivera",   teacherId:"TCH001",
    ela_level:"1 Below", ela_score:461, ela_growth:6,
    math_level:"On",     math_score:491, math_growth:10,
    att:91.7, chronic:false, iep:false, ell:false, ml:false, target:false,
    nys_ela:2, nys_math:2, risk:"Medium",
    ela_gaps:["Fluency","Phonics & Decoding"],
    math_gaps:["Fractions"],
    ela_strengths:["Phonological Awareness"],
    math_strengths:["Number Sense","Operations"],
    sims:[
      { subject:"ELA",  sim1:60, sim2:64, sim3:68, predicted_nys:2 },
      { subject:"Math", sim1:58, sim2:62, sim3:65, predicted_nys:2 },
    ],
    activities:["Choir","Bubble Group"],
  },
  {
    id:"S07", dob:"2012-06-05", name:"Taylor B.",  grade:6, teacher:"S. Kim",      teacherId:"TCH005",
    ela_level:"On",      ela_score:508, ela_growth:15,
    math_level:"1 Below", math_score:472, math_growth:7,
    att:96.1, chronic:false, iep:false, ell:false, ml:false, target:false,
    nys_ela:3, nys_math:3, risk:"Low",
    ela_gaps:["Writing"],
    math_gaps:["Algebra","Geometry"],
    ela_strengths:["Fluency","Reading Comprehension"],
    math_strengths:["Number Sense","Fractions"],
    sims:[
      { subject:"ELA",  sim1:64, sim2:68, sim3:71, predicted_nys:3 },
      { subject:"Math", sim1:56, sim2:60, sim3:63, predicted_nys:2 },
    ],
    activities:["Theater Club","Town Hall Leaders","ASPIRA"],
  },
  {
    id:"S08", dob:"2011-10-19", name:"Jamie F.",   grade:7, teacher:"D. Torres",   teacherId:"TCH006",
    ela_level:"1 Below", ela_score:474, ela_growth:9,
    math_level:"2 Below", math_score:438, math_growth:-1,
    att:90.6, chronic:true,  iep:true,  ell:false, ml:false, target:true,
    iepDate:"2025-11-14", iepGoal:"Develop number sense; reach benchmark on iReady diagnostic",
    nys_ela:2, nys_math:2, risk:"Medium",
    ela_gaps:["Reading Comprehension","Vocabulary"],
    math_gaps:["Number Sense","Fractions","Algebra"],
    ela_strengths:["Phonics & Decoding"],
    math_strengths:[],
    sims:[
      { subject:"ELA",  sim1:52, sim2:55, sim3:58, predicted_nys:2 },
      { subject:"Math", sim1:34, sim2:37, sim3:40, predicted_nys:1 },
    ],
    activities:["Afterschool Target Group","Robotics Club"],
  },
  {
    id:"S09", dob:"2014-03-31", name:"Drew Q.",    grade:4, teacher:"M. Rivera",   teacherId:"TCH001",
    ela_level:"2 Above", ela_score:558, ela_growth:22,
    math_level:"2 Above", math_score:564, math_growth:19,
    att:98.9, chronic:false, iep:false, ell:false, ml:false, target:false,
    nys_ela:4, nys_math:4, risk:"Low",
    ela_gaps:[],
    math_gaps:[],
    ela_strengths:["Reading Comprehension","Vocabulary","Writing"],
    math_strengths:["Algebra","Number Sense","Statistics","Geometry"],
    sims:[
      { subject:"ELA",  sim1:88, sim2:90, sim3:92, predicted_nys:4 },
      { subject:"Math", sim1:86, sim2:89, sim3:91, predicted_nys:4 },
    ],
    activities:["Math Olympiad","Student Council","Robotics Club"],
  },
  {
    id:"S10", dob:"2010-08-11", name:"Avery N.",   grade:8, teacher:"J. Thompson", teacherId:"TCH002",
    ela_level:"1 Below", ela_score:468, ela_growth:4,
    math_level:"On",     math_score:504, math_growth:11,
    att:93.9, chronic:false, iep:false, ell:false, ml:false, target:false,
    nys_ela:2, nys_math:3, risk:"Medium",
    ela_gaps:["Fluency","Reading Comprehension"],
    math_gaps:["Algebra"],
    ela_strengths:["Phonological Awareness","Phonics & Decoding"],
    math_strengths:["Number Sense","Operations","Fractions"],
    sims:[
      { subject:"ELA",  sim1:56, sim2:59, sim3:62, predicted_nys:2 },
      { subject:"Math", sim1:68, sim2:71, sim3:74, predicted_nys:3 },
    ],
    activities:["ASPIRA","Choir"],
  },
  {
    id:"S11", dob:"2013-01-25", name:"Quinn R.",   grade:5, teacher:"M. Rivera",   teacherId:"TCH001",
    ela_level:"2 Below", ela_score:452, ela_growth:-1,
    math_level:"1 Below", math_score:463, math_growth:4,
    att:89.4, chronic:true,  iep:false, ell:false, ml:true,  target:true,
    nys_ela:1, nys_math:1, risk:"High",
    ela_gaps:["Vocabulary","Reading Comprehension","Fluency"],
    math_gaps:["Number Sense","Fractions","Algebra"],
    ela_strengths:[],
    math_strengths:[],
    sims:[
      { subject:"ELA",  sim1:41, sim2:44, sim3:47, predicted_nys:1 },
      { subject:"Math", sim1:43, sim2:46, sim3:49, predicted_nys:1 },
    ],
    activities:["Afterschool Target Group","7% Target Group","ASPIRA"],
  },
  {
    id:"S12", dob:"2012-05-16", name:"Blake S.",   grade:6, teacher:"S. Kim",      teacherId:"TCH005",
    ela_level:"1 Below", ela_score:495, ela_growth:8,
    math_level:"1 Below", math_score:476, math_growth:6,
    att:95.5, chronic:false, iep:false, ell:false, ml:false, target:false,
    nys_ela:2, nys_math:2, risk:"Medium",
    ela_gaps:["Writing","Grammar & Conventions"],
    math_gaps:["Algebra","Geometry"],
    ela_strengths:["Reading Comprehension","Fluency"],
    math_strengths:["Number Sense","Fractions"],
    sims:[
      { subject:"ELA",  sim1:63, sim2:66, sim3:69, predicted_nys:2 },
      { subject:"Math", sim1:59, sim2:63, sim3:66, predicted_nys:2 },
    ],
    activities:["Art Club","Bubble Group","Theater Club"],
  },
];

// ── TEACHER LOOKUP ────────────────────────────────────────────
export const TEACHERS_LIST = [
  { id:"TCH001", name:"M. Rivera",   fullName:"Maria Rivera",    grade:"3-4", subject:"ELA",      color:"#00A878" },
  { id:"TCH002", name:"J. Thompson", fullName:"James Thompson",  grade:"4-5", subject:"Math",     color:"#1560BD" },
  { id:"TCH003", name:"A. Patel",    fullName:"Aisha Patel",     grade:"5-6", subject:"ELA/Math", color:"#7C3AED" },
  { id:"TCH004", name:"C. Brooks",   fullName:"Carlos Brooks",   grade:"6-7", subject:"Math",     color:"#F59E0B" },
  { id:"TCH005", name:"S. Kim",      fullName:"Sarah Kim",       grade:"6",   subject:"ELA",      color:"#E03B3B" },
  { id:"TCH006", name:"D. Torres",   fullName:"David Torres",    grade:"7-8", subject:"Math",     color:"#0891B2" },
];

// ── MATH INTERVENTION GROUPS ──────────────────────────────────
export const MATH_GROUPS = [
  {
    id:"MG01", tier:"Intensive", subject:"Math",
    students:["S01","S08","S11"],
    focusSkills:["Number Sense","Fractions","Algebra"],
    freq:5, sessions:5, avgGain:8,
    history:[{s:"S1",avg:32},{s:"S2",avg:38},{s:"S3",avg:43},{s:"S4",avg:48},{s:"S5",avg:52}],
  },
  {
    id:"MG02", tier:"Strategic", subject:"Math",
    students:["S03","S04","S07","S12"],
    focusSkills:["Fractions","Algebra","Geometry"],
    freq:3, sessions:4, avgGain:12,
    history:[{s:"S1",avg:52},{s:"S2",avg:57},{s:"S3",avg:62},{s:"S4",avg:67}],
  },
  {
    id:"MG03", tier:"Benchmark", subject:"Math",
    students:["S02","S06","S10"],
    focusSkills:["Algebra","Operations"],
    freq:2, sessions:4, avgGain:10,
    history:[{s:"S1",avg:65},{s:"S2",avg:69},{s:"S3",avg:73},{s:"S4",avg:76}],
  },
  {
    id:"MG04", tier:"Extension", subject:"Math",
    students:["S05","S09"],
    focusSkills:["Statistics","Advanced Algebra","Geometry"],
    freq:1, sessions:3, avgGain:16,
    history:[{s:"S1",avg:78},{s:"S2",avg:84},{s:"S3",avg:88}],
  },
];

// ── ELA INTERVENTION GROUPS ───────────────────────────────────
export const ELA_GROUPS = [
  {
    id:"EG01", tier:"Intensive", subject:"ELA",
    students:["S01","S03","S11"],
    focusSkills:["Reading Comprehension","Vocabulary","Fluency"],
    freq:5, sessions:6, avgGain:18,
    history:[{s:"S1",avg:36},{s:"S2",avg:42},{s:"S3",avg:48},{s:"S4",avg:53},{s:"S5",avg:58},{s:"S6",avg:62}],
  },
  {
    id:"EG02", tier:"Strategic", subject:"ELA",
    students:["S02","S06","S08","S10"],
    focusSkills:["Fluency","Reading Comprehension","Writing"],
    freq:3, sessions:4, avgGain:10,
    history:[{s:"S1",avg:56},{s:"S2",avg:61},{s:"S3",avg:65},{s:"S4",avg:68}],
  },
  {
    id:"EG03", tier:"Benchmark", subject:"ELA",
    students:["S04","S07","S12"],
    focusSkills:["Writing","Grammar & Conventions"],
    freq:2, sessions:3, avgGain:11,
    history:[{s:"S1",avg:66},{s:"S2",avg:70},{s:"S3",avg:74}],
  },
  {
    id:"EG04", tier:"Extension", subject:"ELA",
    students:["S05","S09"],
    focusSkills:["Complex Text Analysis","Argumentative Writing"],
    freq:1, sessions:2, avgGain:14,
    history:[{s:"S1",avg:80},{s:"S2",avg:86}],
  },
];

export const ALL_GROUPS = [...ELA_GROUPS, ...MATH_GROUPS];
